import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import StripePaymentForm from '../../../common/WithUi/StripePaymentForm';
import { createStripePaymentIntent, initializeStripe } from '../../../common/WithUi/stripeUtils';
import { useLocation, useNavigate } from 'react-router-dom';
import './SubscriptionPayment.css';
import useApi from '../../../../utils/api-manager/Helper/useApi';
import { useToastHandler } from "../../../common/ToastHandler";
import AuthContext from '../../../../utils/secure-route/AuthContext';

import useFormattingUtils from '../../../../context/useFormattingUtils';
import TrashIcon from '../../../../assets/img/trash-bin.png'
import { ToastContainer } from 'react-toastify';
import BackArrow from '../../../../assets/SubscriptionImage/arrow (1) 1.png'
import { renderModuleIcon } from './moduleIcons'

const PaymentPage = ({
  inModal = false,
  onComplete,
  onBack,
  selectedModules: propModules,
  billingCycle: propBillingCycle,
  storeId,
  couponId,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { Post, Get } = useApi();
  const { formatCurrency } = useFormattingUtils();
  let { user } = useContext(AuthContext);
  const { handleToast } = useToastHandler();

  const fallbackData = location.state || window.paymentPageData || {};

  const getEffectiveCouponId = () => {
    return couponId ||
      fallbackData.couponId ||
      localStorage.getItem('tempCouponId') ||
      null;
  };

  const [showCouponInput, setShowCouponInput] = useState(false);
  const [effectiveCouponId, setEffectiveCouponId] = useState(getEffectiveCouponId());
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [finalAmountData, setFinalAmountData] = useState(null);
  const [modules, setModules] = useState(propModules || fallbackData.selectedModules || []);
  const [moduleSelectedMonths, setModuleSelectedMonths] = useState({});
  const [appliedCouponCode, setAppliedCouponCode] = useState('');
  const [showReferralInput, setShowReferralInput] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [appliedReferralCode, setAppliedReferralCode] = useState('');
  const [referralDiscount, setReferralDiscount] = useState(0);
  const [referralAgentName, setReferralAgentName] = useState('');

  const billingCycle = propBillingCycle || fallbackData.billingCycle || 'Monthly';

  const nonCouponSubtotal = useMemo(() => {
    return modules.reduce((total, module) => {
      const months = moduleSelectedMonths[module.id] || module.selectedMonths || 1;
      const priceDetail = module.price_details?.find(p => p.no_of_months === months) || module.priceDetail;
      return total + parseFloat(priceDetail?.total_price || 0);
    }, 0);
  }, [modules, moduleSelectedMonths]);

  const subtotalValue = couponApplied && finalAmountData
    ? finalAmountData.total_amount
    : nonCouponSubtotal;

  const totalValue = couponApplied && finalAmountData
    ? finalAmountData.final_total
    : nonCouponSubtotal;

  const checkoutTotal = Math.max(0, totalValue * (1 - Number(referralDiscount || 0) / 100));

  const handleBack = () => {
    if (inModal && onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  const formatDuration = (no_of_months, total_days) => {
    if (total_days) {
      if (no_of_months === 12 && total_days === 365) {
        return '';
      }
      const expectedDays = no_of_months * 30;
      const extraDays = total_days - expectedDays;
      if (extraDays > 5) {
        return `+ ${extraDays} day${extraDays > 1 ? 's' : ''}`;
      }
    }
    return '';
  };

  useEffect(() => {
    const newCouponId = getEffectiveCouponId();
    if (newCouponId !== effectiveCouponId) {
      setEffectiveCouponId(newCouponId);
    }
  }, [couponId, fallbackData.couponId]);

  const couponCodeHandler = async (codeToValidate = null, customModuleMonths = null, customModules = null) => {
    const trimmedCouponCode = codeToValidate || couponCode.trim();
    const modulesToUse = customModules || modules;
    const selectedModuleIds = modulesToUse.map(module => module.id);
    const monthsToUse = customModuleMonths || moduleSelectedMonths;

    if (!trimmedCouponCode || selectedModuleIds.length === 0) {
      handleToast({
        type: "ERROR",
        message: "Please enter a valid coupon code and select at least one feature"
      });
      return false;
    }

    try {
      const validationPayload = {
        code: trimmedCouponCode,
        selected_feature_list: selectedModuleIds
      };

      const validationResponse = await Post("checkCouponValidity", validationPayload);
      const couponData = validationResponse?.data;

      if (couponData?.id) {
        setEffectiveCouponId(couponData.id);
      }

      if (couponData?.message === "Coupon is successfuly validated.") {
        try {
          const finalAmountPayload = {
            coupon_id: couponData.id,
            feature_list: selectedModuleIds.map(id => ({
              feature_id: id,
              no_of_months: monthsToUse[id] || 1,
              discount: 0.0
            })),
            store_id: storeId || user.store
          };

          const finalAmountResponse = await Post("getFinalAmout", finalAmountPayload);

          if (finalAmountResponse?.data) {
            const finalData = finalAmountResponse.data;
            setCouponDiscount(finalData.discount_percentage);
            setFinalAmountData(finalData);
            setCouponApplied(true);
            setAppliedCouponCode(trimmedCouponCode);
            return true;
          }
        } catch (error) {
          console.error("Error getting final amount:", error);
          setEffectiveCouponId(null);
          handleToast({
            type: "ERROR",
            message: "Error calculating final amount. Please try again."
          });
          return false;
        }
      } else {
        setEffectiveCouponId(null);
        handleToast({
          type: "ERROR",
          message: couponData?.message || "Error validating coupon. Please try again."
        });
        return false;
      }
    } catch (error) {
      console.error("Error validating coupon:", error);
      setEffectiveCouponId(null);

      const errorMessage = error?.response?.data?.error || "Error validating coupon. Please try again.";

      handleToast({
        type: "ERROR",
        message: errorMessage
      });

      return false;
    }
  };

  const handleApplyCoupon = async () => {
    const trimmedCouponCode = couponCode.trim();

    if (trimmedCouponCode) {
      const isValid = await couponCodeHandler();
      if (isValid) {
        setCouponCode('');
      }
    } else {
      handleToast({
        type: "ERROR",
        message: "Please enter a valid coupon code"
      });
    }
  };

  const handleRemoveCoupon = () => {
    setCouponApplied(false);
    setCouponCode('');
    setAppliedCouponCode('');
    setEffectiveCouponId(null);
    setCouponDiscount(0);
    setFinalAmountData(null);
    setShowCouponInput(true);

    handleToast({
      type: "SUCCESS",
      message: "Coupon removed successfully"
    });
  };

  const handleApplyReferral = async () => {
    const code = referralCode.trim().toUpperCase();
    if (!code) {
      handleToast({ type: "ERROR", message: "Please enter a referral code" });
      return;
    }
    try {
      const response = await Post("validateAgentReferral", { referral_code: code, store_id: storeId || user.store });
      setAppliedReferralCode(response.data.referral_code);
      setReferralDiscount(Number(response.data.discount_percentage || 0));
      setReferralAgentName(response.data.agent_name || 'PaisaVara partner');
      setReferralCode('');
      setShowReferralInput(false);
      handleToast({ type: "SUCCESS", message: `Referral code applied — ${response.data.discount_percentage}% discount` });
    } catch (error) {
      handleToast({ type: "ERROR", message: error.response?.data?.detail || "Invalid referral code" });
    }
  };

  const handleRemoveReferral = () => {
    setAppliedReferralCode('');
    setReferralDiscount(0);
    setReferralAgentName('');
    setShowReferralInput(true);
    handleToast({ type: "SUCCESS", message: "Referral code removed" });
  };

  const handleRemoveModule = async (moduleNameToRemove) => {
    const updatedModules = modules.filter((module) => module.name !== moduleNameToRemove);
    setModules(updatedModules);

    if (updatedModules.length === 0) {
      handleRemoveCoupon();
      return;
    }

    if (!couponApplied || !appliedCouponCode) return;

    try {
      const isValid = await couponCodeHandler(appliedCouponCode, moduleSelectedMonths, updatedModules);
      if (!isValid) {
        handleRemoveCoupon();
      }
    } catch (error) {
      console.error("Error recalculating after module removal:", error);
      handleRemoveCoupon();
      handleToast({
        type: "INFO",
        message: "Coupon was removed due to calculation error"
      });
    }
  };

  const handleModuleMonthChange = async (moduleId, months) => {
    const newModuleSelectedMonths = {
      ...moduleSelectedMonths,
      [moduleId]: months
    };

    setModuleSelectedMonths(newModuleSelectedMonths);

    setModules(prevModules =>
      prevModules.map(module => {
        if (module.id === moduleId) {
          const newPriceDetail = module.price_details.find(p => p.no_of_months === months);
          return {
            ...module,
            selectedMonths: months,
            priceDetail: newPriceDetail || module.priceDetail
          };
        }
        return module;
      })
    );

    if (couponApplied && appliedCouponCode) {
      try {
        await couponCodeHandler(appliedCouponCode, newModuleSelectedMonths);
      } catch (error) {
        console.error("Error recalculating after month change:", error);
        handleToast({
          type: "ERROR",
          message: "Error updating coupon calculation"
        });
      }
    }
  };

  const getModulePrice = (module) => {
    if (couponApplied && finalAmountData) {
      const featureData = finalAmountData.features?.find(f => f.feature === module.id);
      return featureData?.payable_amount || 0;
    }

    const months = moduleSelectedMonths[module.id] || module.selectedMonths || 1;
    const priceDetail = module.price_details?.find(p => p.no_of_months === months) || module.priceDetail;
    return parseFloat(priceDetail?.total_price || 0);
  };

  const getModuleDuration = (module) => {
    const months = moduleSelectedMonths[module.id] || module.selectedMonths || 1;

    if (couponApplied && finalAmountData) {
      const featureData = finalAmountData.features?.find(f => f.feature === module.id);
      if (featureData && featureData.total_days) {
        return `${featureData.total_days} Days`;
      }
    }

    const priceDetail = module.price_details?.find(p => p.no_of_months === months) || module.priceDetail;
    return priceDetail?.days ? `${priceDetail.days} Days` : `${months} ${months > 1 ? 'Months' : 'Month'}`;
  };

  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [showStripeForm, setShowStripeForm] = useState(false);

  // Stripe rejects any PaymentIntent below its per-currency minimum charge
  // (roughly $0.50 USD or the equivalent in most supported currencies) -
  // that's exactly the 500 ("amount must be greater than or equal to the
  // minimum charge amount") seen when a coupon/referral combo discounts an
  // order down near/at $0. There's effectively nothing collectable below
  // that floor, so treat it the same as a fully-free order instead of
  // sending Stripe an amount it will always refuse.
  const STRIPE_MIN_CHARGE_AMOUNT = 0.5;

  const handlePurchase = async () => {
    if (checkoutTotal < STRIPE_MIN_CHARGE_AMOUNT) {
      // Nothing (or nothing chargeable) is owed - skip Stripe entirely and
      // complete the purchase directly; purchaseSubscription doesn't
      // require a payment_intent_id.
      await completePurchase(null);
      return;
    }
    try {
      const paymentIntentData = await createStripePaymentIntent(checkoutTotal);
      const stripe = await initializeStripe(paymentIntentData.stripe_publishable_key);

      setStripePromise(stripe);
      setClientSecret(paymentIntentData.client_secret);
      setShowStripeForm(true);
    } catch (error) {
      handleToast({
        type: 'ERROR',
        message: 'Error initiating payment: ' + (error.response?.data?.message || error.message)
      });
      console.error('Payment initiation error:', error);
    }
  };

  const handleStripeSuccess = async (paymentIntent) => {
    await completePurchase(paymentIntent);
  };

  const completePurchase = async (paymentIntent) => {
    try {
      const newFeatures = modules.map(module => module.name);
      
      const featureList = modules.map((module) => ({
        feature_id: module.id,
        no_of_months: moduleSelectedMonths[module.id] || module.selectedMonths || 1,
        discount: 0.0
      }));

      const payload = {
        store_id: storeId || user.store,
        feature_list: featureList,
        // Coupon discounts are resolved by coupon_id in the gateway. Keeping
        // this at zero prevents the same coupon being deducted twice.
        overall_discount: 0,
        coupon_id: effectiveCouponId,
        referral_code: appliedReferralCode || null,
        payment_intent_id: paymentIntent?.id || null
      };

      const purchaseResponse = await Post("purchaseSubscription", payload);

      if (purchaseResponse?.status === 200 || purchaseResponse?.status === 201) {
        const currentAllowedFeatures = JSON.parse(localStorage.getItem("allowedFeatures") || "[]");
        const temporaryFeatures = [...new Set([...currentAllowedFeatures, ...newFeatures])];

        updateFeaturesAndNotify(temporaryFeatures);

        const userTypeResponse = await Get("checkUserType");

        const finalFeatures = getFinalFeaturesList(
          purchaseResponse.data?.feature_invoice_data?.feature_titles,
          userTypeResponse?.matching_features_titles,
          temporaryFeatures
        );

        updateFeaturesAndNotify(finalFeatures);

        handleToast({
          type: "SUCCESS",
          message: "Payment successful and features purchased",
        });

        setShowStripeForm(false);
        onComplete?.();
        navigate('/');
      } else {
        handleToast({
          type: "ERROR",
          message: "Purchase failed. Please try again.",
        });
      }
    } catch (error) {
      handleToast({
        type: 'ERROR',
        message: 'Error processing payment confirmation'
      });
      console.error('Payment confirmation error:', error);
    }
  };

  const handleStripeError = (error) => {
    handleToast({
      type: 'ERROR',
      message: `Payment failed: ${error}`
    });
    setShowStripeForm(false);
  };

  const updateFeaturesAndNotify = (features) => {
    localStorage.setItem("allowedFeatures", JSON.stringify(features));
    window.dispatchEvent(new Event('allowedFeaturesUpdated'));
  };

  const getFinalFeaturesList = (featureTitles, matchingFeatures, fallbackFeatures) => {
    let allFeatures = [];

    if (Array.isArray(featureTitles)) {
      allFeatures = [...featureTitles];
    }

    if (Array.isArray(matchingFeatures)) {
      matchingFeatures.forEach(feature => {
        if (!allFeatures.includes(feature)) {
          allFeatures.push(feature);
        }
      });
    }

    if (allFeatures.length === 0) {
      allFeatures = fallbackFeatures;
    }

    return allFeatures.flatMap(feat =>
      feat.includes('+') ? feat.split('+').map(f => f.trim()) : feat
    );
  };

  return (
    <div className="new-payment-layout">
      <ToastContainer />
      
      {/* Stripe Payment Modal */}
      {showStripeForm && stripePromise && clientSecret && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div className="stripe-payment-dialog" style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '12px',
            maxWidth: '500px',
            width: '90%',
            position: 'relative'
          }}>
            <button
              onClick={() => setShowStripeForm(false)}
              style={{
                position: 'absolute',
                right: '15px',
                top: '15px',
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer'
              }}
            >
              ×
            </button>
            <h3 style={{ marginBottom: '20px', textAlign: 'center' }}>Complete Payment</h3>
            <Elements stripe={stripePromise}>
              <StripePaymentForm
                clientSecret={clientSecret}
                onSuccess={handleStripeSuccess}
                onError={handleStripeError}
                amount={checkoutTotal}
              />
            </Elements>
          </div>
        </div>
      )}

      <div className="payment-hero-panel">
        <div>
          <span className="payment-eyebrow">Subscription checkout</span>
          <h1>Review your workspace cart</h1>
          <p>Confirm selected modules, apply a coupon if available, and complete your PaisaVara subscription securely.</p>
        </div>
        <button className="payment-back-inline-btn" onClick={handleBack}>
          Back
        </button>
      </div>

      <div className="payment-main-content">
        <div className="cart-preview-container">
          <div className="payment-back-con">
            <button className="payment-back-btn" onClick={handleBack}>
              <img src={BackArrow} alt="Back" className="back-arrow-icons" />
            </button>
          </div>
          <div className="cart-preview-section">
            <h2 className="cart-title">Cart Preview</h2>

            <div className="cart-table">
              <div className="cart-table-header">
                <div className="header-cell title-col">Title</div>
                <div className="header-cell duration-col">Duration</div>
                <div className="header-cell price-col">Discounted Price</div>
                <div className="header-cell discount-col">Discount %</div>
                <div className="header-cell action-col"></div>
              </div>

              <div className="cart-table-body">
                {modules.map((module) => {
                  const currentPrice = getModulePrice(module);
                  const months = moduleSelectedMonths[module.id] || module.selectedMonths || 1;
                  const originalPriceDetail = module.price_details?.find(
                    (p) => p.no_of_months === months
                  );
                  const originalPrice = parseFloat(originalPriceDetail?.total_price || 0);
                  const hasDiscount = currentPrice < originalPrice;
                  const discountPercent = hasDiscount
                    ? Math.round(((originalPrice - currentPrice) / originalPrice) * 100)
                    : 0;

                  return (
                    <div key={module.name} className="cart-table-row">
                      <div className="table-cell title-col">
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <div className="payment-module-icon">
                            {renderModuleIcon(module.name, module.icon)}
                          </div>
                          <span className="payment-module-title">
                            {module.name}
                          </span>
                        </div>
                      </div>

                      <div className="table-cell duration-col">
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <select
                            className="duration-dropdown"
                            value={moduleSelectedMonths[module.id] || module.selectedMonths || 1}
                            onChange={(e) =>
                              handleModuleMonthChange(module.id, Number(e.target.value))
                            }
                          >
                            {module.price_details?.map((priceDetail) => (
                              <option key={priceDetail.id} value={priceDetail.no_of_months}>
                                {priceDetail.no_of_months}{' '}
                                {priceDetail.no_of_months > 1 ? 'Months' : 'Month'}
                              </option>
                            ))}
                          </select>
                          {couponApplied && finalAmountData && (
                            <span style={{ marginLeft: '5px', color: '#007DC4', fontWeight: 'bold' }}>
                              {(() => {
                                const featureData = finalAmountData.features?.find(
                                  f => f.feature === module.id
                                );
                                return featureData
                                  ? formatDuration(featureData.no_of_months, featureData.total_days)
                                  : '';
                              })()}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="table-cell price-col">
                        {formatCurrency(currentPrice.toFixed(2))}
                        {hasDiscount && (
                          <span className="original-price-strike">
                            {formatCurrency(originalPrice.toFixed(2))}
                          </span>
                        )}
                      </div>

                      <div className="table-cell discount-col">
                        {hasDiscount && (
                          <span className="discount-badge">{discountPercent}% off</span>
                        )}
                      </div>

                      <div className="table-cell action-col">
                        <button
                          className="remove-btn"
                          onClick={() => handleRemoveModule(module.name)}
                          title="Remove"
                        >
                          <img src={TrashIcon} alt="Remove" width={16} height={16} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="coupon-section-new">
          <div className="cart-summary-section">
            <h3>Order Summary</h3>

            <div className="summary-row subtotal-row">
              <span>Subtotal:</span>
              <span>{formatCurrency(subtotalValue.toFixed(2))}</span>
            </div>

          <div className="summary-row">
  <span>Discount</span>
  <span>
    {couponApplied && finalAmountData && referralDiscount
      ? `Coupon ${finalAmountData.discount_percentage}% + referral ${referralDiscount}%`
      : couponApplied && finalAmountData
        ? `${finalAmountData.discount_percentage}% coupon`
        : referralDiscount
          ? `${referralDiscount}% referral`
          : '0%'}
  </span>
</div>
<div className="summary-separator" />


            <div className="summary-row total-row">
              <span>Total:</span>
              <span>{formatCurrency(checkoutTotal.toFixed(2))}</span>
            </div>

            {appliedReferralCode && <div className="referral-applied-summary"><div><strong>{appliedReferralCode}</strong><span>{referralAgentName} · {referralDiscount}% partner discount</span></div><button onClick={handleRemoveReferral}>Remove</button></div>}

            <button className="go-to-payment-btn" onClick={handlePurchase}>
              {checkoutTotal < STRIPE_MIN_CHARGE_AMOUNT ? "Complete Order" : "Checkout with Stripe"}
            </button>
          </div>

          <button
            className="payment-coupon-toggle-btn"
            onClick={() => {
              if (couponApplied) {
                handleRemoveCoupon();
                setShowCouponInput(true);
              } else {
                setShowCouponInput(!showCouponInput);
              }
            }}
          >
            {couponApplied
              ? 'Remove Coupon'
              : showCouponInput
                ? 'Hide Coupons'
                : 'Apply Coupons'}
          </button>

          {(!couponApplied && showCouponInput) && (
            <div className="payment-coupon-input-section">
              <input
                type="text"
                placeholder="Enter Code"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.trimStart())}
                onBlur={(e) => setCouponCode(e.target.value.trim())}
                className="payment-coupon-input-field"
                style={{ borderBottom: '1px solid #ced4da' }}
              />
              <button
                onClick={handleApplyCoupon}
                className="payment-apply-coupon-button"
                disabled={!couponCode.trim()}
              >
                Apply Coupon
              </button>
            </div>
          )}

          <button className="payment-coupon-toggle-btn referral-toggle" onClick={() => appliedReferralCode ? handleRemoveReferral() : setShowReferralInput(value => !value)}>
            {appliedReferralCode ? 'Remove Referral Code' : showReferralInput ? 'Hide Referral Code' : 'Have a Referral Code?'}
          </button>
          {!appliedReferralCode && showReferralInput && <div className="payment-coupon-input-section referral-input-section">
            <input type="text" placeholder="Enter agent referral code" value={referralCode} onChange={e=>setReferralCode(e.target.value.trimStart().toUpperCase())} onKeyDown={e=>{if(e.key==='Enter'){e.preventDefault();handleApplyReferral();}}} className="payment-coupon-input-field" />
            <button onClick={handleApplyReferral} className="payment-apply-coupon-button referral-apply-button" disabled={!referralCode.trim()}>Apply Referral</button>
          </div>}
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
